import {
  Box,
  BoxProps,
  Button,
  Card,
  chakra,
  Flex,
  Grid,
  GridItem,
  GridProps,
  HStack,
  Link,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  RiArchive2Line,
  RiArrowUpCircleLine,
  RiLock2Line,
} from "@remixicon/react";
import { forwardRef, ReactNode, useMemo } from "react";

import { RouterLinkButton } from "@/common/components/RouterLinkButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useUnArchiveFeature } from "@/feature/data/useFeatureUpdateMutation";
import { MemberRoleBadge } from "@/global-settings/components/MemberRoleBadge";
import { Flags, useClient, useFlag } from "@reflag/react-sdk";
import { MemberRole, MemberRoles } from "@reflag/shared/permissions";
import { GlobalSettingsUrl } from "@reflag/shared/urls";
import { KeysMatching } from "@reflag/shared/utils/types";

const UpgradeIcon = chakra(RiArrowUpCircleLine);

type LockedCalloutProps = {
  description: ReactNode;
  heading: ReactNode;
  icon: ReactNode;
  textColor?: string;
  actionableContent?: ReactNode;
} & GridProps;

function LockedCallout({
  description,
  heading,
  icon,
  textColor,
  actionableContent,
  ...gridProps
}: LockedCalloutProps) {
  return (
    <Grid
      alignItems={"center"}
      as={Card}
      columnGap={2}
      px={4}
      py={3}
      rowGap={1}
      templateColumns="min-content 1fr min-content"
      variant="filled"
      w="full"
      {...gridProps}
    >
      <GridItem color={textColor}>{icon}</GridItem>
      <GridItem color={textColor}>
        <Flex alignItems="center" display="inline-flex" gap={3}>
          <Text fontWeight={"semibold"} whiteSpace="nowrap">
            {heading}
          </Text>
          <Text color="dimmed" fontSize="sm">
            {description}
          </Text>
        </Flex>
      </GridItem>
      <GridItem>{actionableContent}</GridItem>
    </Grid>
  );
}

export function InsufficientPermissionTooltipLabel({
  role,
}: {
  role: MemberRole;
}) {
  const text =
    role === "publisher"
      ? "You don't have permission to perform this action because it might impact a production environment"
      : "You don't have permission to perform this action";

  return (
    <HStack>
      <Box>
        <RiLock2Line size={14} />
      </Box>
      <Text>{text}</Text>
    </HStack>
  );
}

type Feature = { isArchived: boolean; id: string };

function InsufficientPermissionCallout({ role }: { role: MemberRole }) {
  return (
    <LockedCallout
      description={
        <>
          Only members with the <MemberRoleBadge role={role} /> role can make
          changes.
        </>
      }
      heading="Locked"
      icon={<RiLock2Line size={16} />}
    />
  );
}

function FeatureArchivedTooltipLabel() {
  return (
    <HStack>
      <RiArchive2Line size={14} />
      <Text>Can&apos;t make changes to archived flags.</Text>
    </HStack>
  );
}

function ArchivedFeatureCallout({ featureId }: { featureId: string }) {
  const unarchive = useUnArchiveFeature(featureId);

  return (
    <LockedCallout
      actionableContent={
        <Button size="sm" variant="outline" onClick={() => unarchive()}>
          Unarchive flag
        </Button>
      }
      description="Can't make changes to archived flags."
      heading="Archived"
      icon={<RiArchive2Line size={16} />}
    />
  );
}

function InsufficientAccessPlanTooltip({
  minimumPlan,
}: {
  minimumPlan: string;
}) {
  return (
    <HStack>
      <RiArrowUpCircleLine size={14} />
      <Text>
        Only available on the <strong>{minimumPlan}</strong> plan
      </Text>
    </HStack>
  );
}

type AccessPlanRestrictedFlagConfigPayload = {
  upgradeMessage?: string;
  upgradeLink?: string;
};

type AccessPlanRestrictedFlags = KeysMatching<
  Flags,
  { config: { payload: AccessPlanRestrictedFlagConfigPayload | null } }
>;

function InsufficientAccessPlanCallout({
  minimumPlan,
  planDetails,
}: {
  minimumPlan: string;
  planDetails: AccessPlanRestrictedFlagConfigPayload;
}) {
  const { env } = useEnvironment();

  const color = useColorModeValue("brand.500", "brand.400");

  return (
    <LockedCallout
      actionableContent={
        <RouterLinkButton
          size="sm"
          to={planDetails?.upgradeLink || GlobalSettingsUrl(env, "org-billing")}
          variant="outline"
        >
          Go to billing
        </RouterLinkButton>
      }
      description={
        planDetails?.upgradeMessage || (
          <>
            Upgrade to{" "}
            <Link fontWeight={"bold"} href="https://reflag.com/pricing">
              {minimumPlan}
            </Link>{" "}
            to unlock this flag
          </>
        )
      }
      heading={"Upgrade to unlock"}
      icon={<UpgradeIcon size={16} />}
      textColor={color}
    />
  );
}

const Inert = forwardRef<HTMLDivElement, { children: ReactNode } & BoxProps>(
  ({ children, ...rest }, ref) => (
    <Box
      ref={ref}
      display="contents"
      sx={{ "> *": { filter: "opacity(0.6)" } }}
      {...{ inert: "" }}
      {...rest}
    >
      {children}
    </Box>
  ),
);

Inert.displayName = "Inert";

type RestrictedProps = {
  alertStyle?: "callout" | "tooltip" | "none";
  displayContents?: "inert" | "normal" | "hidden";
  children: ReactNode;
  _tooltipWrapper?: BoxProps;
} & (
  | {
      role: MemberRole;
      flagKey?: AccessPlanRestrictedFlags;
      feature?: Feature;
    }
  | {
      role?: MemberRole;
      flagKey: AccessPlanRestrictedFlags;
      feature?: Feature;
    }
  | {
      role?: MemberRole;
      flagKey?: AccessPlanRestrictedFlags;
      feature: Feature | undefined;
    }
);

export function Restricted({
  role,
  feature,
  flagKey: guardedFlagKey,
  alertStyle = "tooltip",
  displayContents = "inert",
  children,
  _tooltipWrapper,
}: RestrictedProps) {
  const featureClient = useClient();
  const { isEnabled: guardedFlagEnabled, config: guardedFlagConfig } =
    !!guardedFlagKey && featureClient
      ? featureClient.getFlag(guardedFlagKey)
      : { isEnabled: false, config: undefined };

  const { org, overriddenRole } = useEnvironment();
  const { isEnabled: enforcePermissions } = useFlag("user-permissions");
  const actualRole = enforcePermissions
    ? (overriddenRole ?? org.userRole)
    : "admin";

  const { callout, tooltip, display } = useMemo(() => {
    if (role) {
      const canModify =
        MemberRoles.indexOf(actualRole) >= MemberRoles.indexOf(role);

      if (!canModify) {
        return {
          callout: <InsufficientPermissionCallout role={role} />,
          tooltip: <InsufficientPermissionTooltipLabel role={role} />,
          display: displayContents,
        };
      }
    }

    if (feature?.isArchived) {
      return {
        callout: <ArchivedFeatureCallout featureId={feature.id} />,
        tooltip: <FeatureArchivedTooltipLabel />,
        display: displayContents,
      };
    }

    if (guardedFlagKey) {
      if (!guardedFlagEnabled) {
        return {
          callout: guardedFlagConfig?.key ? (
            <InsufficientAccessPlanCallout
              minimumPlan={guardedFlagConfig.key}
              planDetails={guardedFlagConfig.payload}
            />
          ) : undefined,
          tooltip: guardedFlagConfig?.key ? (
            <InsufficientAccessPlanTooltip
              minimumPlan={guardedFlagConfig.key}
            />
          ) : undefined,
          display: !guardedFlagConfig?.key ? "hidden" : displayContents,
        };
      }
    }

    return { display: "normal" };
  }, [
    role,
    feature,
    guardedFlagKey,
    guardedFlagEnabled,
    guardedFlagConfig,
    actualRole,
    displayContents,
  ]);

  if (!callout && !tooltip) {
    return children;
  }

  let actualContent = children;

  if (display === "hidden") {
    actualContent = null;
  }

  if (display === "inert") {
    actualContent = <Inert>{children}</Inert>;
  }

  if (alertStyle === "callout") {
    return (
      <>
        {callout}
        {actualContent}
      </>
    );
  } else if (alertStyle === "tooltip") {
    return (
      <Tooltip label={tooltip} openDelay={300} hasArrow>
        <Box {..._tooltipWrapper}>{actualContent}</Box>
      </Tooltip>
    );
  } else {
    return actualContent;
  }
}
